import type {
  SearchRequestApi,
  UpdateResourceByIdV2MutationRequestApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import {
  searchFilterFilterType,
  searchOperator,
  searchRequestSortOrder,
} from '@ingenico/api-sdk-device-management-private-v2';
import {
  DatagridQuickFilterTypeEnum,
  type DataGridSimpleQuickFilter,
  type DefaultQuickFilterCommonProps,
} from '@ingenico/react-ui/advanced-search';
import { DATAGRID_ROWS_PER_PAGE_OPTIONS } from '@ingenico/react-ui/datagrid';
import type { GridSortModel, GridInitialState } from '@mui/x-data-grid-premium';
import { isEmpty, isEqual, toString } from 'lodash-es';
import type { ResourceListItemFront } from '../../domain/models/resources.model';
import type { EditResourceFormValues } from './ResourcesList.model';

export const HEADER_HEIGHT_RESOURCES_TO_REMOVE_FROM_DATAGRID = 395;

export const defaultResourcesGridSortModel: GridSortModel = [
  {
    field: 'creationDate',
    sort: 'desc',
  },
];

export const resourcesGridInitialState: GridInitialState = {
  pagination: { paginationModel: { pageSize: DATAGRID_ROWS_PER_PAGE_OPTIONS[0], page: 0 } },
  sorting: {
    sortModel: defaultResourcesGridSortModel,
  },
};

export function buildResourceTypeRequestFilter(quickfilterValue: string) {
  return {
    filterType: searchFilterFilterType.array,
    name: 'resource.extension',
    operator: searchOperator.in,
    value: JSON.stringify([quickfilterValue]),
  };
}

export function computeFilters({
  searchQuery,
  quickfilterValue,
}: {
  readonly searchQuery: string;
  readonly quickfilterValue: string;
}): SearchRequestApi['filters'] {
  const searchQueryFilter = isEmpty(searchQuery)
    ? []
    : [
        {
          filterType: searchFilterFilterType.fulltext,
          name: '',
          operator: searchOperator.equals,
          value: `*${searchQuery}*`,
        },
      ];

  const quickFilterComputed = isEmpty(quickfilterValue) ? [] : [buildResourceTypeRequestFilter(quickfilterValue)];

  return [...searchQueryFilter, ...quickFilterComputed];
}

export function transformRequestField(field: string): string {
  switch (field) {
    case 'sizeBytes':
      return 'resource.size';
    default:
      return `resource.${field}`;
  }
}

export function computeSort([sort]: GridSortModel): SearchRequestApi['sort'] {
  if (isEmpty(sort)) {
    return {
      field: transformRequestField(defaultResourcesGridSortModel[0].field),
      order: searchRequestSortOrder.DESC,
    };
  }

  return {
    field: transformRequestField(sort.field),
    order: isEqual(sort.sort, 'asc') ? searchRequestSortOrder.ASC : searchRequestSortOrder.DESC,
  };
}

export function isResourceOwnedByOrganization({
  currentOrganizationId,
  resourceOrganizationId,
}: {
  readonly currentOrganizationId?: string;
  readonly resourceOrganizationId?: string;
}): boolean {
  const normalizedCurrentOrganizationId = toString(currentOrganizationId).trim().toLowerCase();
  const normalizedResourceOrganizationId = toString(resourceOrganizationId).trim().toLowerCase();

  if (isEmpty(normalizedCurrentOrganizationId) || isEmpty(normalizedResourceOrganizationId)) {
    return false;
  }

  return normalizedCurrentOrganizationId === normalizedResourceOrganizationId;
}

export function mapResourceListItemFrontToUpdateRequest(
  resource: ResourceListItemFront,
): UpdateResourceByIdV2MutationRequestApi {
  const trimmedDescription = resource.description?.trim();
  return {
    name: resource.name,
    ...(trimmedDescription !== null && trimmedDescription !== undefined ? { description: trimmedDescription } : {}),
  };
}

export function mapResourceToFormValues(resource?: ResourceListItemFront): EditResourceFormValues {
  return {
    name: resource?.name ?? '',
    description: resource?.description ?? '',
    file: {
      filename: resource?.filename ?? '',
      mimeType: resource?.mimeType ?? '',
      sizeBytes: resource?.sizeBytes ?? 0,
    },
  };
}

export function buildEmptyResourceQuickFilter(
  quickFilter: DefaultQuickFilterCommonProps,
  idx: number,
): DataGridSimpleQuickFilter {
  const { label, field, order, selfOption: isSelfOption, searchMode } = quickFilter;
  const { operator: condition } = buildResourceTypeRequestFilter('type');
  return {
    order: order ?? idx,
    label,
    type: DatagridQuickFilterTypeEnum.AutoComplete,
    input: { criteria: [], condition, field },
    selfOption: isSelfOption,
    searchMode,
  } as DataGridSimpleQuickFilter;
}
